import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, Send, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { formatMoney, formatDate } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";

export const SettlementDetailDialog = ({ id, open, onOpenChange }) => {
  const { t } = useT();
  const { can } = useAuth();
  const qc = useQueryClient();
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [confirmPay, setConfirmPay] = useState(false);

  const { data: s } = useQuery({
    queryKey: ["settlement", id], enabled: open,
    queryFn: async () => (await api.get(`/settlements/${id}`)).data,
  });

  const toggleLine = useMutation({
    mutationFn: ({ work_order_id, incluir, motivo }) =>
      api.patch(`/settlements/${id}/lines?incluir=${incluir}`, { work_order_id, motivo: motivo || "Reincluida" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settlement", id] }),
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  const approve = useMutation({
    mutationFn: () => api.post(`/settlements/${id}/approve`),
    onSuccess: () => { toast.success(t("finance.settlements.approved")); qc.invalidateQueries({ queryKey: ["settlement", id] }); qc.invalidateQueries({ queryKey: ["settlements"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  const registerPayment = useMutation({
    mutationFn: () => api.post(`/settlements/${id}/register-payment`, { tipo: "efectivo" }),
    onSuccess: () => { toast.success(t("finance.settlements.paid_ok")); qc.invalidateQueries({ queryKey: ["settlement", id] }); qc.invalidateQueries({ queryKey: ["settlements"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  const sendReceipt = useMutation({
    mutationFn: () => api.post(`/settlements/${id}/send-receipt`),
    onSuccess: () => toast.success(t("finance.settlements.receipt_sent")),
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  const downloadReceipt = async () => {
    const res = await api.get(`/settlements/${id}/receipt.pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `${s.code}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!s) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="settlement-detail-dialog" className="max-h-[85vh] max-w-2xl overflow-y-auto rounded-[10px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {s.code}
              <Badge variant="outline">{t(`finance.settlements.status.${s.estado}`)}</Badge>
            </DialogTitle>
          </DialogHeader>

          <p className="text-[12.5px] text-ink-tertiary">{s.sub_nombre} · {t("finance.settlements.cutoff")} {formatDate(s.corte_fecha)}</p>

          <div className="rounded-[8px] border border-line">
            <p className="border-b border-line px-3 py-1.5 text-[12px] font-semibold text-ink">{t("finance.settlements.lines")}</p>
            <table className="w-full text-[12px]" data-testid="settlement-lines-table">
              <tbody>
                {s.lineas.map((l) => (
                  <tr key={l.work_order_id} className={`border-b border-line ${!l.incluido ? "opacity-50" : ""}`}>
                    <td className="px-3 py-1.5 w-6">
                      {s.estado === "borrador" && (
                        <Checkbox data-testid={`line-toggle-${l.work_order_id}`} checked={l.incluido}
                          onCheckedChange={(v) => toggleLine.mutate({ work_order_id: l.work_order_id, incluir: Boolean(v),
                            motivo: v ? undefined : window.prompt(t("finance.settlements.exclude_reason_ph")) || "Excluida" })} />
                      )}
                    </td>
                    <td className="px-3 py-1.5">{l.code} · {l.property_nombre}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{formatMoney(l.monto_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!!s.no_liquidables?.length && (
            <div className="rounded-[8px] border border-state-warn-soft bg-state-warn-soft/40" data-testid="settlement-no-liquidables">
              <p className="border-b border-state-warn-soft px-3 py-1.5 text-[12px] font-semibold text-ink">
                {t("finance.settlements.not_liquidable_title")}
              </p>
              <table className="w-full text-[12px]">
                <tbody>
                  {s.no_liquidables.map((nl) => (
                    <tr key={nl.work_order_id} data-testid={`settlement-no-liquidable-${nl.work_order_id}`} className="border-b border-state-warn-soft last:border-0">
                      <td className="px-3 py-1.5">
                        <a href={`/ordenes/${nl.work_order_id}`} target="_blank" rel="noreferrer" className="font-medium text-brand hover:text-brand-hover">
                          {nl.code}
                        </a>
                        <span className="text-ink-tertiary"> · {nl.property_nombre}</span>
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{formatMoney(nl.monto_cents)}</td>
                      <td className="px-3 py-1.5 text-ink-tertiary">{nl.motivo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="space-y-1 rounded-[8px] border border-line p-3 text-[13px]">
            <div className="flex justify-between"><span>{t("finance.settlements.gross")}</span><span className="tabular-nums">{formatMoney(s.total_bruto_cents)}</span></div>
            <div className="flex justify-between text-ink-tertiary"><span>{t("finance.settlements.advances_applied")}</span><span className="tabular-nums">-{formatMoney(s.total_anticipos_cents)}</span></div>
            <div className="flex justify-between text-ink-tertiary"><span>{t("finance.settlements.prior_payments")}</span><span className="tabular-nums">-{formatMoney(s.total_pagos_previos_cents)}</span></div>
            <div className="flex justify-between border-t border-line pt-1 font-semibold"><span>{t("finance.settlements.net_to_pay")}</span><span data-testid="settlement-net-total" className="tabular-nums">{formatMoney(s.neto_a_pagar_cents)}</span></div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button data-testid="settlement-receipt-download" variant="outline" size="sm" onClick={downloadReceipt}>
              <Download className="mr-1.5 h-3.5 w-3.5" />{t("finance.settlements.receipt")}
            </Button>
            {can("finance", "update") && (
              <Button data-testid="settlement-send-receipt" variant="outline" size="sm" disabled={sendReceipt.isPending} onClick={() => sendReceipt.mutate()}>
                <Send className="mr-1.5 h-3.5 w-3.5" />{t("finance.settlements.send_receipt")}
              </Button>
            )}
            {s.estado === "borrador" && can("finance", "approve") && (
              <Button data-testid="settlement-approve-button" className="bg-brand hover:bg-brand-hover" size="sm" onClick={() => setConfirmApprove(true)}>
                <Check className="mr-1.5 h-3.5 w-3.5" />{t("finance.settlements.approve")}
              </Button>
            )}
            {s.estado === "aprobada" && can("finance", "create") && (
              <Button data-testid="settlement-register-payment-button" className="bg-state-ok hover:bg-state-ok" size="sm" onClick={() => setConfirmPay(true)}>
                {t("finance.settlements.register_payment")}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog testId="settlement-approve-confirm" open={confirmApprove} onOpenChange={setConfirmApprove}
        title={t("finance.settlements.approve")} description={t("finance.settlements.approve_confirm")}
        onConfirm={() => { approve.mutate(); setConfirmApprove(false); }} />
      <ConfirmDialog testId="settlement-pay-confirm" open={confirmPay} onOpenChange={setConfirmPay}
        title={t("finance.settlements.register_payment")} description={formatMoney(s.neto_a_pagar_cents)}
        onConfirm={() => { registerPayment.mutate(); setConfirmPay(false); }} />
    </>
  );
};
